'use client';
import { useState, forwardRef, useRef, useImperativeHandle } from 'react';
import { cn } from '@fshr/shared-react';
import Icon from '../icon';
import { fuzzy } from '@fshr/shared';
//import { useTranslations } from '@fshr/context/i18n-context';

export type TypeAheadFilterResult = {
	original: string;
	string?: string;
	score?: number;
	index?: number;
};

export type TypeAheadOption = {
	label: string;
	value: string;
	href?: string;
	display?: (currentValue: string, result: TypeAheadFilterResult) => React.ReactNode;
};

interface TypeAheadProps extends React.InputHTMLAttributes<HTMLInputElement> {
	name?: string;
	id: string;
	options: TypeAheadOption[];
	filter?: (item: string) => boolean;
	noResults?: React.ReactNode | ((currentValue: string) => React.ReactNode);
	className?: string;
	wrapperClassName?: string;
	inputClassName?: string;
	resultButtonWrapperClassName?: string;
	placeholder?: string;
	label?: string;
	errorMessage?: string;
	readOnly?: boolean;
	required?: boolean;
	clearResultsButton?: boolean;
	value?: string;
	onChange?: (e: any) => void;
	onBlur?: (e: any) => void;
	showHighlightedResults?: boolean;
}

const TypeAhead = forwardRef(
	(
		{
			id,
			name,
			value = '',
			label,
			errorMessage,
			options,
			filter = () => false,
			noResults = '',
			className,
			wrapperClassName,
			inputClassName,
			resultButtonWrapperClassName,
			placeholder,
			readOnly,
			required,
			clearResultsButton = false,
			onChange,
			onBlur,
			showHighlightedResults = false,
			...props
		}: TypeAheadProps,
		ref: React.Ref<HTMLInputElement | null>
	) => {
		//const t = useTranslations();
		const t = text => text;
		const inputRef = useRef<HTMLInputElement | null>(null);
		useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
		const resultLiRefs = useRef<Array<HTMLLIElement | null>>([]);

		const [showOptions, setShowOptions] = useState(false);

		const initialDisplayValue =
			options.find(({ value: toFind }) => toFind === value)?.label || value;
		const [displayValue, setDisplayValue] = useState(initialDisplayValue);

		const [selectedIndex, setSelectedIndex] = useState(-1);
		const [isSelected, setIsSelected] = useState(false);
		const [focusOptions, setFocusOptions] = useState(false);

		const defaultResultsArr: TypeAheadFilterResult[] = options.map(({ label: optionLabel }) => {
			return { original: optionLabel };
		});

		const getOptionFromResult = (result: TypeAheadFilterResult) => {
			if (!result?.original) {
				return;
			}
			return options.find(({ label: optionLabel }) => optionLabel === result.original);
		};

		const getResults = (searchValue: string): TypeAheadFilterResult[] => {
			return searchValue
				? fuzzy
						.filter(
							searchValue,
							options.map(({ label: optionLabel }) => optionLabel),
							{ pre: '<mark>', post: '</mark>' }
						)
						.filter(({ score }) => !searchValue || score > 0)
						.filter(result => !filter(result.original))
				: defaultResultsArr;
		};
		const [results, setResults] = useState(
			value ? getResults(initialDisplayValue) : defaultResultsArr
		);

		const select = () => {
			const result = results[selectedIndex];
			if (!result) {
				return;
			}
			handleInputChange(result.original);
			setShowOptions(false);
			const option = getOptionFromResult(result);
			if (option?.href) {
				window.location.href = option.href;
			}
		};

		function change(direction: number) {
			let index =
				direction > 0 && selectedIndex === results.length - 1 ? 0 : selectedIndex + direction;
			if (index < 0) index = results.length - 1;
			setFocusOptions(true);
			setSelectedIndex(index);
			resultLiRefs.current[index]?.scrollIntoView({
				behavior: 'instant',
				block: 'nearest',
			});
		}

		const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
			if (
				event.relatedTarget?.id !== `${id}-icon` &&
				event.relatedTarget?.parentElement?.parentElement?.id !== `${id}-listbox`
			) {
				event.preventDefault();
				setSelectedIndex(-1);
				setShowOptions(false);
				onBlur?.({
					currentTarget: {
						name,
						id,
						value,
					},
					data: { displayValue: event.currentTarget.value },
				});
			}
		};

		const handleInputChange = (newValue = '') => {
			setResults(getResults(newValue));
			setDisplayValue(newValue);
			setSelectedIndex(-1);
			onChange?.({
				currentTarget: {
					name,
					id,
					value:
						options.find(
							({ label: optionLabel }) => optionLabel.toLowerCase() === newValue.toLowerCase()
						)?.value || '',
				},
				data: { displayValue: newValue },
			});
		};

		const renderNoResults = () => {
			if (!noResults) return null;
			if (typeof noResults === 'function') {
				return noResults(displayValue);
			}
			if (typeof noResults === 'string') {
				return (
					<div className="flex items-start bg-surface-raised-subtle">
						<span className="text-body2 w-full py-100 px-150 text-start">{noResults}</span>
					</div>
				);
			}
			return noResults;
		};

		const renderResultContent = (result: TypeAheadFilterResult): React.ReactNode | string => {
			const option = getOptionFromResult(result);
			if (option?.display) {
				return option.display(displayValue, result);
			}
			return showHighlightedResults && result.string ? result.string : result.original;
		};

		return (
			<div
				className={cn('relative w-full', wrapperClassName, {
					'pt-200': label,
					'text-red-700': errorMessage,
					'pb-[28px]': readOnly,
					'pb-[20px] lg:pb-[28px]': !readOnly,
				})}
				role="combobox"
				aria-haspopup="listbox"
				aria-owns={`${id}-listbox`}
				aria-expanded={showOptions}
				aria-controls={`${id}-listbox`}
				id={`${id}-typeahead`}
			>
				<div
					className={cn(
						[
							'relative',
							'flex',
							'gap-100',
							'items-center',
							'focus:outline-hidden',
							'border-black',
							'h-[46px]',
						],
						className,
						{
							'border-b bg-surface-form px-4': !readOnly,
							'placeholder-red-700 border-red-700': errorMessage,
						}
					)}
				>
					<input
						className={cn(
							[
								'peer',
								'text-body2',
								'focus:outline-hidden',
								'w-full',
								'placeholder:transition-opacity',
								'placeholder:opacity-100!',
								'placeholder:duration-300!',
								'placeholder:text-black!',
								'focus:placeholder:opacity-0!',
								'bg-transparent',
							],
							inputClassName,
							{
								'placeholder:text-red-700!': errorMessage,
							}
						)}
						id={id}
						ref={inputRef}
						placeholder={placeholder && required ? `${placeholder}*` : placeholder}
						required={required}
						aria-autocomplete="list"
						aria-controls={`${id}-listbox`}
						aria-labelledby={label && `${id}-label`}
						aria-activedescendant={
							selectedIndex >= 0 && results.length > 0 ? `${id}-result-${selectedIndex}` : undefined
						}
						onInput={() => {
							isSelected && setIsSelected(false);
							setShowOptions(true);
						}}
						onClick={() => {
							isSelected && setIsSelected(false);
							setShowOptions(!showOptions);
						}}
						value={displayValue}
						autoComplete="off"
						spellCheck="false"
						onKeyDown={e => {
							if (results.length === 0) return;
							switch (e.key) {
								case 'Enter':
									e.preventDefault();
									select();
									break;
								case 'ArrowDown':
									e.preventDefault();
									if (!showOptions) {
										setShowOptions(true);
									}
									change(1);
									break;
								case 'ArrowUp':
									e.preventDefault();
									change(-1);
									break;
								case 'Escape':
									e.preventDefault();
									setShowOptions(false);
									break;
							}
						}}
						readOnly={readOnly}
						onChange={(e: React.FormEvent<HTMLInputElement>) => {
							e.preventDefault();
							handleInputChange(e.currentTarget.value);
						}}
						onBlur={handleInputBlur}
						{...props}
						aria-invalid={errorMessage ? true : undefined}
						aria-describedby={errorMessage ? `${id}-error` : props['aria-describedby']}
					/>
					{label && (
						<label
							className="text-label uppercase absolute duration-300 opacity-100 left-0 top-0 -translate-y-[18px] peer-placeholder-shown:opacity-0 peer-placeholder-shown:translate-y-0 peer-focus:opacity-100 peer-focus:-translate-y-[18px]"
							htmlFor={id}
							id={id && `${id}-label`}
						>
							{`${label}${required ? '*' : ''}`}
						</label>
					)}

					{!readOnly && (
						<>
							{clearResultsButton ? (
								<button
									type="button"
									className="lg:text-[20px]"
									onClick={
										value
											? () => {
													setIsSelected(false);
												}
											: () => {
													setShowOptions(!showOptions);
												}
									}
									aria-label={t(value ? 'Clear' : 'Options')}
									title={t(value ? 'Clear' : 'Options')}
									id={`${id}-icon`}
								>
									<Icon icon={value ? 'close' : 'search'}></Icon>
								</button>
							) : (
								<span
									className="lg:text-[20px]"
									aria-label={t(clearResultsButton && value ? 'Clear' : 'Options')}
									title={t(clearResultsButton && value ? 'Clear' : 'Options')}
									id={`${id}-icon`}
								>
									<Icon icon={clearResultsButton && value ? 'close' : 'search'}></Icon>
								</span>
							)}
						</>
					)}
				</div>
				<ul
					className="absolute z-10 max-h-[282px] w-full overflow-auto bg-white"
					role="listbox"
					aria-labelledby={`${id}-label`}
					id={`${id}-listbox`}
				>
					{showOptions &&
						(results.length > 0
							? results.map((result, i) => {
									const resultContent = renderResultContent(result);
									const setAsHtml = showHighlightedResults && typeof resultContent === 'string';
									const handleClickResult = () => {
										inputRef.current?.focus();
										handleInputChange(result.original);
										setIsSelected(true);
										setShowOptions(false);
										const option = getOptionFromResult(result);
										if (option?.href) {
											window.location.href = option.href;
										}
									};
									return (
										<li
											key={i}
											role="option"
											id={`${id}-result-${i}`}
											className={cn('flex items-start cursor-pointer bg-surface-raised-subtle', {
												'hover:bg-surface-form': !focusOptions,
												'bg-surface-form': focusOptions && selectedIndex === i,
											})}
											aria-selected={selectedIndex === i}
											ref={el => {
												resultLiRefs.current[i] = el;
											}}
											onMouseOver={() => {
												setSelectedIndex(i);
												focusOptions && setFocusOptions(false);
											}}
										>
											<button
												tabIndex={-1}
												type="button"
												className={cn(
													'text-body2 w-full py-100 px-150 text-start',
													{
														'[&_mark]:bg-transparent [&_mark]:font-bold [&_mark]:text-inherit':
															showHighlightedResults,
													},
													resultButtonWrapperClassName
												)}
												key={i}
												onClick={handleClickResult}
												onTouchEnd={handleClickResult}
												dangerouslySetInnerHTML={setAsHtml ? { __html: resultContent } : undefined}
											>
												{!setAsHtml ? resultContent : null}
											</button>
										</li>
									);
								})
							: renderNoResults())}
				</ul>
				{errorMessage && (
					<span
						id={`${id}-error`}
						className="absolute left-0 lg:pt-[2px] text-caption text-red-700"
						role="alert"
					>
						{errorMessage}
					</span>
				)}
			</div>
		);
	}
);

export default TypeAhead;
